import { secondsToMilliseconds } from 'date-fns';
import { useEffect, useState } from 'react';
import { useAuthorizationServerInfoQuery } from '../queries/useAuthorizationServerInfoQuery';
import { getMsToTokenExpiration, isTokenPossiblyValid, } from '../tokens/tokenWithExpiration';
import { useIntervalEffect } from './useIntervalEffect';
import { useRefreshTokensHandler } from './useRefreshTokensHandler';
// I guessed these
const REFRESH_CHECK_INTERVAL = secondsToMilliseconds(10);
const MIN_TOKEN_EXPIRATION_REFRESH_THRESHOLD = secondsToMilliseconds(30);
/**
 * Defines (Token expiration / Denominator)
 * e.g. if denominator = 4 then a token with 300s expiration
 * will be refreshed after it's 75s before expiration
 */
const TOKEN_EXPIRATION_REFRESH_THRESHOLD_DENOMINATOR = 4;
const getShouldRefreshAccessToken = (accessToken) => {
    const expiresIn = getMsToTokenExpiration(accessToken) ?? 0;
    const calculatedExpirationThreshold = accessToken.validForMs / TOKEN_EXPIRATION_REFRESH_THRESHOLD_DENOMINATOR;
    const expirationThreshold = Math.max(calculatedExpirationThreshold, MIN_TOKEN_EXPIRATION_REFRESH_THRESHOLD);
    return expiresIn < expirationThreshold;
};
export const useTokenManager = ({ tokenStorage, issuerUrl, clientCredentials, }) => {
    const [accessToken, setAccessToken] = useState(undefined);
    const [refreshToken, setRefreshToken] = useState(undefined);
    const [isAccessTokenExpired, setIsAccessTokenExpired] = useState(false);
    const [hasValidRefreshToken, setHasValidRefreshToken] = useState(false);
    const { data: authorizationServer } = useAuthorizationServerInfoQuery(issuerUrl);
    const reloadTokenState = () => {
        const storedAccessToken = tokenStorage.accessToken.get();
        if (!storedAccessToken) {
            setAccessToken(undefined);
            setIsAccessTokenExpired(false);
        }
        else {
            setAccessToken(storedAccessToken.token);
            const isAccessTokenValid = isTokenPossiblyValid(storedAccessToken);
            setIsAccessTokenExpired(!isAccessTokenValid);
        }
        const storedRefreshToken = tokenStorage.refreshToken.get();
        if (!storedRefreshToken) {
            setRefreshToken(undefined);
            setHasValidRefreshToken(false);
        }
        else {
            setRefreshToken(storedRefreshToken.token);
            const isRefreshTokenValid = isTokenPossiblyValid(storedRefreshToken);
            setHasValidRefreshToken(isRefreshTokenValid);
        }
    };
    const { handleRefreshTokens, isRefreshingTokens } = useRefreshTokensHandler({
        tokenStorage,
        authorizationServer,
        clientCredentials,
        onSettled: () => {
            reloadTokenState();
        },
    });
    const tryToRefresh = () => {
        reloadTokenState();
        const storedAccessToken = tokenStorage.accessToken.get();
        const shouldRefreshTokens = !storedAccessToken || getShouldRefreshAccessToken(storedAccessToken);
        if (shouldRefreshTokens) {
            handleRefreshTokens();
        }
    };
    useIntervalEffect(tryToRefresh, REFRESH_CHECK_INTERVAL);
    // Run first refresh immediately and whenever authorization server is loaded
    useEffect(() => {
        tryToRefresh();
    }, [authorizationServer]);
    const hasValidAccessToken = !!accessToken && !isAccessTokenExpired;
    const removeTokens = () => {
        tokenStorage.clear();
    };
    return {
        accessToken,
        refreshToken,
        isAccessTokenExpired,
        isRefreshingTokens,
        hasValidAccessToken,
        hasValidRefreshToken,
        updateTokenState: reloadTokenState,
        handleRefreshTokens,
        removeTokens,
    };
};
